import React from 'react';
import { Form, Input } from 'antd';
import { FileOutlined, SendOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
} from '@/pages/inside/tenant/style';

/**
 * Add blocker modal with RichTextEditor attachments (same UX as create sprint / create task).
 */
export default function BlockerCreateModal({
    form,
    open,
    onCancel,
    onSubmit,
    submitPending,
    blockerPendingAttachments,
    pendingLinkAttachments,
    handleBlockerFilesSelected,
    removeBlockerAttachment,
    handleLinkRequest,
    handleLinkModalSubmit,
    linkModalOpen,
    setLinkModalOpen,
    linkModalForm,
    linkModalCallbackRef,
    mentionUsers,
    mentionMilestones,
    mentionSprints,
    truncateFileName,
}) {
    const title = <ModalChromeHeading>Add blocker</ModalChromeHeading>;

    const linkTitle = <ModalChromeHeading>Add link</ModalChromeHeading>;

    return (
        <>
            <AppModal
                size="md"
                title={title}
                open={open}
                onCancel={onCancel}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button $primary={false} $size="14px" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            loading={submitPending}
                            icon={<SendOutlined />}
                            onClick={onSubmit}
                        >
                            Add
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={form} layout="vertical" requiredMark={false}>
                        <Form.Item
                            name="title"
                            label={<ModalBlockLabel>Title</ModalBlockLabel>}
                            rules={[{ required: true, message: 'Enter title' }]}
                        >
                            <Input placeholder="e.g. API dependency" size="large" />
                        </Form.Item>
                        <Form.Item name="description" label={<ModalBlockLabel>Description</ModalBlockLabel>}>
                            <RichTextEditor
                                placeholder="Add description — bold, lists, @mentions. Attach files or links."
                                showAttachButton
                                mentionUsers={mentionUsers}
                                mentionMilestones={mentionMilestones}
                                mentionSprints={mentionSprints}
                                onFilesSelected={handleBlockerFilesSelected}
                                onLinkRequest={handleLinkRequest}
                                onAttachmentRemoved={(index) => {
                                    const p = blockerPendingAttachments[index];
                                    if (p) removeBlockerAttachment(p.id);
                                }}
                            />
                        </Form.Item>
                        {blockerPendingAttachments.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                    marginTop: 8,
                                    marginBottom: 16,
                                    padding: '8px 0',
                                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                }}
                            >
                                {blockerPendingAttachments.map((p) => {
                                    const isImg = (p.type || '').startsWith('image/');
                                    return (
                                        <div
                                            key={p.id}
                                            style={{
                                                position: 'relative',
                                                width: isImg ? 64 : 'auto',
                                                height: isImg ? 64 : 36,
                                                borderRadius: 8,
                                                overflow: 'hidden',
                                                border: '1px solid var(--ant-color-border)',
                                                background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
                                            }}
                                        >
                                            {!isImg && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '0 8px',
                                                        height: '100%',
                                                        maxWidth: 180,
                                                    }}
                                                >
                                                    <FileOutlined style={{ color: 'var(--ant-color-text-secondary)' }} />
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                        title={p.name}
                                                    >
                                                        {truncateFileName(p.name)}
                                                    </span>
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                aria-label="Remove attachment"
                                                onClick={() => removeBlockerAttachment(p.id)}
                                                style={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    border: 'none',
                                                    background: 'rgba(0,0,0,0.5)',
                                                    color: '#fff',
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {pendingLinkAttachments.length > 0 && open && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                                {pendingLinkAttachments.map(({ id, url, title: linkTitleText }) => (
                                    <a key={id} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                                        {linkTitleText || url}
                                    </a>
                                ))}
                            </div>
                        )}
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <AppModal
                size="sm"
                title={linkTitle}
                open={linkModalOpen}
                onCancel={() => {
                    setLinkModalOpen(false);
                    linkModalCallbackRef.current = null;
                }}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button
                            $primary={false}
                            $size="14px"
                            onClick={() => {
                                setLinkModalOpen(false);
                                linkModalCallbackRef.current = null;
                            }}
                        >
                            Cancel
                        </Button>
                        <Button $primary $blackOnLight $size="14px" icon={<SendOutlined />} onClick={handleLinkModalSubmit}>
                            Add link
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={linkModalForm} layout="vertical" requiredMark={false}>
                        <Form.Item name="url" label={<ModalBlockLabel>URL</ModalBlockLabel>} rules={[{ required: true, message: 'Enter URL' }]}>
                            <Input placeholder="https://..." size="large" />
                        </Form.Item>
                        <Form.Item name="title" label={<ModalBlockLabel>Link text</ModalBlockLabel>}>
                            <Input placeholder="Optional display text" size="large" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>
        </>
    );
}
